import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportStep } from '../../hooks/report/types';
import SingleReportCard from './SingleReportCard';
import ReportFeedback from './ReportFeedback';
import { getStatusText, formatProcessingTime } from '../../utils/reportProcessingUtils';
import { PartialResultRenderer, PartialResult } from './display/PartialResultRenderer';
import './PartialSummary.css';
import '../../styles/safeDomErrorStyles.css';

interface ProModeQuery {
  iteration: number;
  query: string;
  purpose: string;
  row_count: number;
  execution_time_ms: number;
  columns?: string[];
  rows?: any[][];
  preview_truncated?: boolean;
}

interface ReportResultsProps {
  progress?: any;
  allReports: ReportStep[];
  reportResults: {[key: number]: any};
  loadingReportIndices: number[];
  reportsWithPartialResults?: number[];
  partialResults?: {[key: number]: {name: string; content: PartialResult; isFinal: boolean}};
  onReloadReport: (reportIndex: number) => Promise<boolean>;
  summary?: any;
  summaryPartialResult?: {content: any; isFinal: boolean} | null;
  proModeQueries?: {[reportIndex: number]: ProModeQuery[]};
  isProMode?: boolean;
  isCollectingData?: boolean;
  reportId?: number;
  reportFeedback?: string | null;
}

/**
 * ReportResults component - displays the results of reports
 */
const ReportResults: React.FC<ReportResultsProps> = ({
  allReports,
  reportResults,
  loadingReportIndices,
  reportsWithPartialResults = [],
  partialResults = {},
  onReloadReport,
  summary,
  summaryPartialResult,
  proModeQueries = {},
  isProMode = false,
  isCollectingData = false,
  reportId,
  reportFeedback,
}) => {
  const { t } = useTranslation('create');

  // Check if we have a summary
  const hasSummary = summary !== undefined && summary !== null;

  // State for expanding/collapsing SQL queries inside each card
  const [expandedCardQueries, setExpandedCardQueries] = useState<{[reportIndex: number]: boolean}>({});
  const [expandedSqlItems, setExpandedSqlItems] = useState<{[key: string]: boolean}>({});
  
  // Filter out deleted reports and sort by index
  const filteredReports = allReports.filter(report => !report.deleted);
  const sortedReports = [...filteredReports].sort((a, b) => a.index - b.index);
  
  return (
    <div className="report-results">
      
      {/* Summary view - moved to the top */}
      {hasSummary && (
        <div className="summary-view">
          {reportId && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
              <ReportFeedback
                reportId={reportId}
                initialFeedback={reportFeedback ?? null}
              />
            </div>
          )}

          {summary.report_title && (
            <h2>{summary.report_title}</h2>
          )}
          
          {summary.summary && (
            <div className="summary-content">
              <h4>{t('results.summary_heading')}</h4>
              <p>{summary.summary}</p>
            </div>
          )}
          
          {summary.key_findings && summary.key_findings.length > 0 && (
            <div className="key-findings">
              <h4>{t('results.key_findings')}</h4>
              <ul>
                {summary.key_findings.map((finding: string, index: number) => (
                  <li key={index}>{finding}</li>
                ))}
              </ul>
            </div>
          )}
          
          {summary.recommendations && summary.recommendations.length > 0 && (
            <div className="recommendations">
              <h4>{t('results.recommendations')}</h4>
              <ul>
                {summary.recommendations.map((recommendation: string, index: number) => (
                  <li key={index}>{recommendation}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {/* Partial Summary Results removed - now displayed in ProcessingStep */}
      
      {/* Pro Mode: Data Collection Phase queries (report_index = -1) */}
      {isProMode && proModeQueries[-1] && proModeQueries[-1].length > 0 && (
        <div className="pro-collection-phase">
          <div
            className="pro-queries-inline-header"
            onClick={() => setExpandedCardQueries(prev => ({ ...prev, [-1]: !(prev[-1] ?? false) }))}
          >
            <span className="pro-queries-toggle">{expandedCardQueries[-1] ? '\u25BC' : '\u25B6'}</span>
            <span className="pro-badge">PRO</span>
            <span>{t('processing.pro_collection_phase')}</span>
            <span className="pro-queries-count">
              {t('results.queries_count', { count: proModeQueries[-1].length })}
            </span>
          </div>
          {expandedCardQueries[-1] && (
            <div className="pro-queries-list">
              {proModeQueries[-1].map((q, i) => {
                const sqlKey = `collection_${i}`;
                const isSqlExpanded = expandedSqlItems[sqlKey] ?? false;
                const hasTableData = q.columns && q.columns.length > 0 && q.rows && q.rows.length > 0;
                return (
                  <div key={i} className="pro-query-item">
                    <div
                      className="pro-query-meta pro-query-meta-clickable"
                      onClick={() => setExpandedSqlItems(prev => ({ ...prev, [sqlKey]: !isSqlExpanded }))}
                    >
                      <span className="pro-query-toggle">{isSqlExpanded ? '\u25BC' : '\u25B6'}</span>
                      <span className="pro-query-iteration">#{q.iteration}</span>
                      <span className="pro-query-purpose">{q.purpose}</span>
                      <span className="pro-query-stats">{t('results.query_stats', { rows: q.row_count, ms: q.execution_time_ms })}</span>
                    </div>
                    {isSqlExpanded && (
                      <>
                        <pre className="pro-query-sql"><code>{q.query}</code></pre>
                        {hasTableData && (
                          <div className="pro-query-result-table-wrapper">
                            <table className="pro-query-result-table">
                              <thead>
                                <tr>
                                  {q.columns!.map((col, ci) => (
                                    <th key={ci}>{col}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {q.rows!.map((row, ri) => (
                                  <tr key={ri}>
                                    {row.map((cell: any, ci: number) => (
                                      <td key={ci}>{cell != null ? String(cell) : ''}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {q.preview_truncated && (
                              <div className="pro-query-table-note">
                                {t('results.rows_preview', { count: q.row_count })}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Display all reports */}
      <div className="all-reports-container">
        {sortedReports.map(report => {
          const reportIndex = report.index;
          const reportResult = reportResults[reportIndex];
          const isLoading = loadingReportIndices.includes(reportIndex);
          
          return (
            <div
              key={reportIndex}
              id={`report-${reportIndex}`}
              className="report-container"
            >
              {/* Removed duplicate title */}
              
              {report.status === 'pending' && (
                <div className="report-pending">
                  <div className="pending-icon">⏳</div>
                  <p>{t('results.pending')}</p>
                </div>
              )}
              
              {report.status === 'processing' && reportsWithPartialResults.includes(reportIndex) && partialResults[reportIndex] && (
                <PartialResultRenderer
                  result={partialResults[reportIndex].content}
                  reportName={partialResults[reportIndex].name}
                />
              )}
              
              {report.status === 'processing' && !reportsWithPartialResults.includes(reportIndex) && (
                <div className="report-processing">
                  <div className="report-writing-animation">
                    <div className="typing-container">
                      <div className="typing-text">
                        {isProMode && isCollectingData
                          ? t('results.processing_collecting')
                          : isProMode
                            ? t('results.processing_generating')
                            : t('results.processing_working')}
                      </div>
                    </div>

                    {/* Chart Animation */}
                    <div className="chart-animation-container">
                      <div className="chart-bar" style={{"--random-height": "60%"} as React.CSSProperties}></div>
                      <div className="chart-bar" style={{"--random-height": "80%"} as React.CSSProperties}></div>
                      <div className="chart-bar" style={{"--random-height": "40%"} as React.CSSProperties}></div>
                      <div className="chart-bar" style={{"--random-height": "70%"} as React.CSSProperties}></div>
                      <div className="chart-bar" style={{"--random-height": "50%"} as React.CSSProperties}></div>
                      <div className="chart-line"></div>
                      <div className="chart-point"></div>
                      <div className="chart-point"></div>
                      <div className="chart-point"></div>
                      <div className="chart-point"></div>
                      <div className="chart-point"></div>
                    </div>

                    <div className="processing-message">
                      {isProMode && isCollectingData
                        ? t('results.processing_agent_collecting')
                        : t('results.processing_analyzing')}
                    </div>
                  </div>
                </div>
              )}

              {/* Pro Mode SQL queries inline inside this card */}
              {isProMode && proModeQueries[reportIndex] && proModeQueries[reportIndex].length > 0 && (
                <div className="pro-queries-inline">
                  <div
                    className="pro-queries-inline-header"
                    onClick={() => setExpandedCardQueries(prev => ({ ...prev, [reportIndex]: !(prev[reportIndex] ?? false) }))}
                  >
                    <span className="pro-queries-toggle">{expandedCardQueries[reportIndex] ? '\u25BC' : '\u25B6'}</span>
                    <span className="pro-badge">PRO</span>
                    <span>{t('results.agent_queries')}</span>
                    <span className="pro-queries-count">
                      {t('results.queries_count', { count: proModeQueries[reportIndex].length })}
                    </span>
                  </div>
                  {expandedCardQueries[reportIndex] && (
                    <div className="pro-queries-list">
                      {proModeQueries[reportIndex].map((q, i) => {
                        const sqlKey = `${reportIndex}_${i}`;
                        const isSqlExpanded = expandedSqlItems[sqlKey] ?? false;
                        const hasTableData = q.columns && q.columns.length > 0 && q.rows && q.rows.length > 0;
                        return (
                          <div key={i} className="pro-query-item">
                            <div
                              className="pro-query-meta pro-query-meta-clickable"
                              onClick={() => setExpandedSqlItems(prev => ({ ...prev, [sqlKey]: !isSqlExpanded }))}
                            >
                              <span className="pro-query-toggle">{isSqlExpanded ? '\u25BC' : '\u25B6'}</span>
                              <span className="pro-query-iteration">#{q.iteration}</span>
                              <span className="pro-query-purpose">{q.purpose}</span>
                              <span className="pro-query-stats">{t('results.query_stats', { rows: q.row_count, ms: q.execution_time_ms })}</span>
                            </div>
                            {isSqlExpanded && (
                              <>
                                <pre className="pro-query-sql"><code>{q.query}</code></pre>
                                {hasTableData && (
                                  <div className="pro-query-result-table-wrapper">
                                    <table className="pro-query-result-table">
                                      <thead>
                                        <tr>
                                          {q.columns!.map((col, ci) => (
                                            <th key={ci}>{col}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {q.rows!.map((row, ri) => (
                                          <tr key={ri}>
                                            {row.map((cell: any, ci: number) => (
                                              <td key={ci}>{cell != null ? String(cell) : ''}</td>
                                            ))}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {q.preview_truncated && (
                                      <div className="pro-query-table-note">
                                        {t('results.rows_preview', { count: q.row_count })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              
              {report.status === 'success' && (
                <>
                  {reportResult ? (
                    <SingleReportCard 
                      report={report}
                      reportResult={reportResult}
                      isAdmin={false}
                      getStatusText={getStatusText}
                      isLoading={isLoading}
                      onReloadRequest={onReloadReport}
                    />
                  ) : (
                    <div className="report-loading">
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                      </div>
                      <p>{t('results.loading_data')}</p>
                      <button
                        className="reload-button"
                        onClick={() => onReloadReport(reportIndex)}
                        disabled={isLoading}
                      >
                        {isLoading ? t('results.loading') : t('results.load_data')}
                      </button>
                    </div>
                  )}
                </>
              )}
              
              {report.status === 'error' && (
                <div className="report-error-container">
                  <div className="error-icon">❌</div>
                  <p>{t('results.error_processing')}</p>
                  <p className="error-message">{report.error || t('results.unknown_error')}</p>
                  <button
                    className="retry-button"
                    onClick={() => onReloadReport(reportIndex)}
                    disabled={isLoading}
                  >
                    {isLoading ? t('results.loading') : t('processing.error_retry')}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Summary view removed from here - now only appears at the top */}
      
      {/* Empty state */}
      {allReports.length === 0 && (
        <div className="no-reports">
          <p>{t('results.no_reports')}</p>
        </div>
      )}
    </div>
  );
};

export default ReportResults;
